import Statement from './Statement';

import { Vector, HashSet } from 'prelude-ts';
import {
  Replace,
  Remove,
  ReplaceSplit,
  AddResource,
  When,
  Solve,
  Match,
  Negate,
  ReplaceSplitFilter,
  FormulaFilter
} from './Action';
import Tensor from './formula/Tensor';
import Plus from './formula/Plus';
import Formula from './formula/Formula';
import Lollipop from './formula/Lollipop';
import With from './formula/With';
import Bottom from './formula/Bottom';
import One from './formula/One';
import Par from './formula/Par';

export abstract class Rule {
  abstract apply(to: Statement): Vector<Statement>;
  abstract name(): String;
}

export class RuleApplication {
  readonly rule: Rule;
  readonly to: Statement;
  readonly appResult: Vector<Statement>;
  readonly result: Vector<Statement>;

  constructor(
    rule: Rule,
    to: Statement,
    appResult: Vector<Statement>,
    result: Vector<Statement>
  ) {
    this.rule = rule;
    this.to = to;
    this.appResult = appResult;
    this.result = result;
  }

  equals(that: RuleApplication) {
    return this.result.equals(that.result);
  }
}
export class DefineRule extends Rule {
  name(): String {
    return '';
  }

  apply(to: Statement): Vector<Statement> {
    return Vector.of(to);
  }
}

export class Highlight extends Rule {
  readonly point: Formula['id'] | undefined;

  constructor(point: Formula['id'] | undefined) {
    super();
    this.point = point;
  }

  apply(_to: Statement): Vector<Statement> {
    throw new Error('Tried to apply highlight rule');
  }
  name(): String {
    return 'highlight';
  }
}

abstract class ParRule extends Rule {
  readonly par: Par;

  constructor(par: Par) {
    super();
    this.par = par;
  }
}

export class ParRight extends ParRule {
  name(): String {
    return '⅋R';
  }

  apply(to: Statement): Vector<Statement> {
    return new Replace(
      this.par.id,
      Vector.of(this.par.left, this.par.right)
    ).apply(to);
  }
}

export class ParLeft extends ParRule {
  /** Formulae that go to the left branch. */
  private readonly left: HashSet<Formula['id']>;

  /** Formulae that go to the right branch. */
  private readonly right: HashSet<Formula['id']>;

  constructor(
    par: Par,
    left: HashSet<Formula['id']>,
    right: HashSet<Formula['id']>
  ) {
    super(par);
    this.left = left;
    this.right = right;
  }

  apply(to: Statement): Vector<Statement> {
    const keepLeft: FormulaFilter = id => this.left.contains(id);
    const keepRight: FormulaFilter = id => this.right.contains(id);

    const leftBranch: [Formula, FormulaFilter] = [this.par.left, keepLeft];
    const rightBranch: [Formula, FormulaFilter] = [this.par.right, keepRight];

    return new ReplaceSplitFilter(
      this.par.id,
      Vector.of(leftBranch, rightBranch)
    ).apply(to);
  }

  name(): String {
    return '⅋L';
  }
}

/**
 * This is used by `FormulaBox` to signal that we want to apply the
 * `ParLeft` rule. Since `FormulaBox` does not know how to split
 * the resources, this message is intercepted by `Subgoal`, which
 * fills in the missing pieces and turns it into a `ParLeft`
 * instance.
 */
export class PreParLeft extends ParRule {
  apply(_to: Statement): Vector<Statement> {
    throw Error('Tried to apply to PreParLeft.');
  }

  name(): String {
    return '⅋L-pre';
  }
}

abstract class TensorRule extends Rule {
  readonly tensor: Tensor;

  constructor(tensor: Tensor) {
    super();
    this.tensor = tensor;
  }
}

export class TensorLeft extends TensorRule {
  apply(to: Statement): Vector<Statement> {
    return new Replace(
      this.tensor.id,
      Vector.of(this.tensor.left, this.tensor.right)
    ).apply(to);
  }

  name(): String {
    return '⊗L';
  }
}

export class TensorRight extends TensorRule {
  /** Formulae that go to the left branch. */
  private readonly left: HashSet<Formula['id']>;

  /** Formulae that go to the right branch. */
  private readonly right: HashSet<Formula['id']>;

  constructor(
    tensor: Tensor,
    left: HashSet<Formula['id']>,
    right: HashSet<Formula['id']>
  ) {
    super(tensor);
    this.left = left;
    this.right = right;
  }

  apply(to: Statement): Vector<Statement> {
    const keepLeft: FormulaFilter = id => this.left.contains(id);
    const keepRight: FormulaFilter = id => this.right.contains(id);

    const leftBranch: [Formula, FormulaFilter] = [this.tensor.left, keepLeft];
    const rightBranch: [Formula, FormulaFilter] = [
      this.tensor.right,
      keepRight
    ];

    return new ReplaceSplitFilter(
      this.tensor.id,
      Vector.of(leftBranch, rightBranch)
    ).apply(to);
  }

  name(): String {
    return '⊗R';
  }
}

/**
 * This is used by `FormulaBox` to signal that we want to apply the
 * `TensorRight` rule. Since `FormulaBox` does not know how to split
 * the resources, this message is intercepted by `Subgoal`, which
 * fills in the missing pieces and turns it into a `TensorRight`
 * instance.
 */
export class PreTensorRight extends TensorRule {
  apply(_to: Statement): Vector<Statement> {
    throw Error('Tried to apply to PreTensorRight.');
  }

  name(): String {
    return '⊗R-pre';
  }
}

abstract class PlusRule extends Rule {
  protected readonly plus: Plus;

  constructor(plus: Plus) {
    super();
    this.plus = plus;
  }
}

export class PlusLeft extends PlusRule {
  apply(to: Statement): Vector<Statement> {
    return new ReplaceSplit(
      this.plus.id,
      Vector.of(this.plus.left, this.plus.right)
    ).apply(to);
  }

  name(): String {
    return '⊕L';
  }
}

export class PlusRight extends PlusRule {
  readonly choice: Formula;
  constructor(plus: Plus, choice: Formula) {
    super(plus);
    this.choice = choice;
  }

  apply(to: Statement): Vector<Statement> {
    return new Replace(this.plus.id, Vector.of(this.choice)).apply(to);
  }

  name(): String {
    return '⊕R';
  }
}

abstract class LolliRule {
  protected readonly lolli: Lollipop;
  constructor(lolli: Lollipop) {
    this.lolli = lolli;
  }
}

export class LolliLeft extends LolliRule {
  private readonly lhs: Formula;
  constructor(lolli: Lollipop, lhs: Formula) {
    super(lolli);
    this.lhs = lhs;
  }

  apply(to: Statement): Vector<Statement> {
    return new Remove(this.lhs.id)
      .andThen(new Replace(this.lolli.id, Vector.of(this.lolli.right)))
      .apply(to);
  }

  name(): String {
    return '⊸L';
  }
}

export class LolliRight extends LolliRule {
  apply(to: Statement): Vector<Statement> {
    return new Replace(this.lolli.id, Vector.of(this.lolli.right))
      .andThen(new AddResource(this.lolli.left))
      .apply(to);
  }

  name(): String {
    return '⊸R';
  }
}

abstract class WithRule extends Rule {
  protected readonly with: With;
  constructor(withOp: With) {
    super();
    this.with = withOp;
  }
}

export class WithLeft extends WithRule {
  private readonly choice: Formula;

  constructor(withOp: With, choice: Formula) {
    super(withOp);
    this.choice = choice;
  }
  apply(to: Statement): Vector<Statement> {
    return new Replace(this.with.id, Vector.of(this.choice)).apply(to);
  }

  name(): String {
    return '\\&L';
  }
}

export class WithRight extends WithRule {
  apply(to: Statement): Vector<Statement> {
    return new ReplaceSplit(
      this.with.id,
      Vector.of(this.with.left, this.with.right)
    ).apply(to);
  }
  name(): String {
    return '\\&R';
  }
}

abstract class BotRule extends Rule {
  protected readonly bot: Bottom;
  protected readonly filter: (stmt: Statement) => boolean;
  constructor(bot: Bottom, filter: (stmt: Statement) => boolean) {
    super();
    this.bot = bot;
    this.filter = filter;
  }
}

export class BotLeft extends BotRule {
  apply(to: Statement): Vector<Statement> {
    return new When(this.filter, new Solve()).apply(to);
  }

  name(): String {
    return '⊥L';
  }
}

export class BotRight extends BotRule {
  apply(to: Statement): Vector<Statement> {
    return new When(this.filter, new Remove(this.bot.id)).apply(to);
  }

  name(): String {
    return '⊥R';
  }
}

abstract class OneRule extends Rule {
  protected readonly one: One;
  protected readonly filter: (stmt: Statement) => boolean;
  constructor(one: One, filter: (stmt: Statement) => boolean) {
    super();
    this.one = one;
    this.filter = filter;
  }
}

export class OneLeft extends OneRule {
  apply(to: Statement): Vector<Statement> {
    return new When(this.filter, new Remove(this.one.id)).apply(to);
  }

  name(): String {
    return '1L';
  }
}

export class OneRight extends OneRule {
  apply(to: Statement): Vector<Statement> {
    return new When(this.filter, new Solve()).apply(to);
  }

  name(): String {
    return '1R';
  }
}

export class TopRule extends Rule {
  apply(to: Statement): Vector<Statement> {
    return new Solve().apply(to);
  }

  name(): String {
    return '⊤';
  }
}

export class ZeroRule extends Rule {
  apply(to: Statement): Vector<Statement> {
    return new Solve().apply(to);
  }

  name(): String {
    return '0';
  }
}

export class AxiomRule extends Rule {
  private readonly left: Formula;
  private readonly right: Formula;

  constructor(left: Formula, right: Formula) {
    super();
    this.left = left;
    this.right = right;
  }

  apply(to: Statement): Vector<Statement> {
    return new Match(this.left.id, this.right.id).apply(to);
  }

  name(): String {
    return 'Axiom';
  }
}

export class Negation extends Rule {
  private readonly formula: Formula;

  constructor(formula: Formula) {
    super();
    this.formula = formula;
  }

  apply(to: Statement): Vector<Statement> {
    return new Negate(this.formula.id).apply(to);
  }

  name(): String {
    return 'Negation';
  }
}

export type RuleExecutor = (rule: Rule) => void;
